import React, { useState } from 'react';
import { BookOpen, ChevronLeft, ChevronDown, ChevronRight, Code, Zap, Target, Clock, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { NavLink } from 'react-router-dom';

interface DocumentationProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Documentation: React.FC<DocumentationProps> = ({ addNotification }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const isExpanded = (sectionId: string) => expandedSections.has(sectionId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <NavLink
              to="/resource"
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              title="Back to Resource Center"
            >
              <ChevronLeft className="h-5 w-5 text-slate-300" />
            </NavLink>
            <BookOpen className="h-8 w-8 text-purple-400" />
            <div>
              <h1 className="text-2xl font-bold text-slate-100">Documentation</h1>
              <p className="text-slate-300 mt-1">Technical documentation and implementation guides</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Table of Contents</h2>
        <div className="space-y-2">
          <a href="#overview" className="block text-purple-400 hover:text-purple-300">1. Overview</a>
          <a href="#processing-flow" className="block text-purple-400 hover:text-purple-300">2. Processing Flow</a>
          <a href="#improvements" className="block text-purple-400 hover:text-purple-300">3. Key Improvements</a>
          <a href="#architecture" className="block text-purple-400 hover:text-purple-300">4. Architecture Components</a>
          <a href="#content-selection" className="block text-purple-400 hover:text-purple-300">5. Content Selection Strategy</a>
          <a href="#performance" className="block text-purple-400 hover:text-purple-300">6. Performance Benefits</a>
          <a href="#implementation" className="block text-purple-400 hover:text-purple-300">7. Implementation Details</a>
          <a href="#usage" className="block text-purple-400 hover:text-purple-300">8. Usage Example</a>
          <a href="#future" className="block text-purple-400 hover:text-purple-300">9. Future Enhancements</a>
          <a href="#test-context" className="block text-purple-400 hover:text-purple-300">10. Test Context</a>
        </div>
      </div>

      {/* Overview */}
      <div id="overview" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Zap className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🚀 Hybrid Document Processing Approach</h2>
        </div>
        
        <div className="prose max-w-none">
          <p className="text-slate-200 mb-4">
            The hybrid approach combines multiple strategies to efficiently process documents for test case generation, 
            avoiding the inefficiency of sending full document content to the LLM for every test case.
          </p>
          
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-200 mb-2">Key Benefits</h3>
            <ul className="text-blue-200 space-y-1">
              <li>• 60-80% reduction in token usage</li>
              <li>• 3-5x faster processing for multiple techniques</li>
              <li>• Higher quality test cases from relevant content</li>
              <li>• Better scalability for large documents</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Processing Flow */}
      <div id="processing-flow" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Target className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🔄 Processing Flow</h2>
        </div>

        <div className="space-y-6">
          {/* Phase 1 */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Phase 1: Document Structure Analysis</h3>
            <div className="bg-slate-700 rounded p-3 mb-3">
              <code className="text-sm text-slate-200">PDF Upload → Extract Full Content → Analyze Structure → Cache Results</code>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-slate-200">
              <li>Extract complete document once using <code className="bg-slate-600 px-1 rounded text-slate-200">pdfplumber</code></li>
              <li>Split into logical sections (pages, headers, paragraphs, lists)</li>
              <li>Extract keywords and topics for each section</li>
              <li>Build content index for fast keyword lookup</li>
              <li>Cache the structure to avoid re-analysis</li>
            </ol>
          </div>

          {/* Phase 2 */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Phase 2: Intelligent Content Selection</h3>
            <div className="bg-slate-700 rounded p-3 mb-3">
              <code className="text-sm text-slate-200">Test Type + Technique → Search Terms → Relevant Sections → Targeted Content</code>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-slate-200">
              <li>Analyze test type requirements (factual, procedural, conceptual)</li>
              <li>Generate search terms based on test type and technique</li>
              <li>Score and rank sections by relevance</li>
              <li>Select top 5 most relevant sections</li>
              <li>Combine into targeted content (max 2000 chars)</li>
            </ol>
          </div>

          {/* Phase 3 */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Phase 3: Targeted Generation</h3>
            <div className="bg-slate-700 rounded p-3 mb-3">
              <code className="text-sm text-slate-200">Relevant Content → LLM Call → Test Cases → Cache Results</code>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-slate-200">
              <li>Send only relevant content to LLM</li>
              <li>Generate test cases using specific technique prompts</li>
              <li>Cache generation results to avoid re-processing</li>
              <li>Optimize token usage per generation</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Key Improvements */}
      <div id="improvements" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <TrendingUp className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">📊 Key Improvements</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Before */}
          <div className="border border-red-700 rounded-lg p-4 bg-red-900/30">
            <h3 className="text-lg font-semibold text-red-200 mb-3 flex items-center">
              <XCircle className="h-5 w-5 mr-2" />
              Before (Inefficient)
            </h3>
            <ul className="space-y-2 text-red-200">
              <li>❌ Full document (1500-2000 chars) sent for every test case</li>
              <li>❌ Multiple overlapping LLM calls</li>
              <li>❌ No content targeting</li>
              <li>❌ High token usage</li>
              <li>❌ Generic test cases</li>
            </ul>
          </div>

          {/* After */}
          <div className="border border-green-700 rounded-lg p-4 bg-green-900/30">
            <h3 className="text-lg font-semibold text-green-200 mb-3 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              After (Hybrid Approach)
            </h3>
            <ul className="space-y-2 text-green-200">
              <li>✅ Document analyzed once and cached</li>
              <li>✅ Targeted content selection per test type/technique</li>
              <li>✅ Intelligent section scoring based on relevance</li>
              <li>✅ Reduced token usage by 60-80%</li>
              <li>✅ Higher quality test cases from relevant content</li>
              <li>✅ Faster processing through caching</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Architecture Components */}
      <div id="architecture" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Code className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🏗️ Architecture Components</h2>
        </div>

        <div className="space-y-4">
          {/* DocumentSection Class */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">DocumentSection Class</h3>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm">
{`@dataclass
class DocumentSection:
    page_number: int
    section_title: str
    content: str
    word_count: int
    section_type: str  # 'header', 'paragraph', 'list', 'table'
    relevance_keywords: List[str]`}
              </pre>
            </div>
          </div>

          {/* DocumentStructure Class */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">DocumentStructure Class</h3>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm">
{`@dataclass
class DocumentStructure:
    sections: List[DocumentSection]
    total_pages: int
    total_words: int
    document_hash: str
    main_topics: List[str]
    content_index: Dict[str, List[DocumentSection]]`}
              </pre>
            </div>
          </div>

          {/* Caching System */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Caching System</h3>
            <ul className="space-y-2 text-slate-200">
              <li>• <strong>Document Cache:</strong> Stores analyzed document structures</li>
              <li>• <strong>Generation Cache:</strong> Stores generated test cases</li>
              <li>• <strong>Hash-based keys:</strong> MD5 of document content</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content Selection Strategy */}
      <div id="content-selection" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Target className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🎯 Content Selection Strategy</h2>
        </div>

        <div className="space-y-4">
          {/* Search Term Generation */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Search Term Generation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">Test Type Terms:</h4>
                <ul className="space-y-1 text-slate-200">
                  <li>• <strong>Factual</strong> → ['what', 'who', 'when', 'where', 'definition']</li>
                  <li>• <strong>Procedural</strong> → ['how', 'step', 'process', 'procedure']</li>
                  <li>• <strong>Conceptual</strong> → ['why', 'explain', 'reason', 'cause']</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-200 mb-2">Technique Terms:</h4>
                <ul className="space-y-1 text-slate-200">
                  <li>• <strong>Scenario</strong> → ['scenario', 'situation', 'case', 'example']</li>
                  <li>• <strong>Edge Case</strong> → ['edge', 'boundary', 'limit', 'extreme']</li>
                  <li>• <strong>Negative</strong> → ['error', 'invalid', 'wrong', 'failure']</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section Scoring Algorithm */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Section Scoring Algorithm</h3>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm">
{`score = 0
for term in search_terms:
    if term in section.content:
        score += 1
    if term in section.title:
        score += 2
if section.type in ['header', 'numbered_list']:
    score += 1
if section.word_count > 100:
    score += 1`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Benefits */}
      <div id="performance" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Clock className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">📈 Performance Benefits</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Token Usage Reduction */}
          <div className="border border-blue-700 rounded-lg p-4 bg-blue-900/30">
            <h3 className="text-lg font-semibold text-blue-200 mb-3">Token Usage Reduction</h3>
            <ul className="space-y-2 text-blue-200">
              <li><strong>Before:</strong> ~2000 tokens × number of techniques</li>
              <li><strong>After:</strong> ~2000 tokens total + ~500-1000 per technique</li>
              <li><strong>Savings:</strong> 60-80% reduction</li>
            </ul>
          </div>

          {/* Processing Speed */}
          <div className="border border-green-700 rounded-lg p-4 bg-green-900/30">
            <h3 className="text-lg font-semibold text-green-200 mb-3">Processing Speed</h3>
            <ul className="space-y-2 text-green-200">
              <li><strong>First call:</strong> Document analysis + generation</li>
              <li><strong>Subsequent calls:</strong> Cached analysis + targeted generation</li>
              <li><strong>Speed improvement:</strong> 3-5x faster</li>
            </ul>
          </div>

          {/* Quality Improvement */}
          <div className="border border-purple-700 rounded-lg p-4 bg-purple-900/30">
            <h3 className="text-lg font-semibold text-purple-200 mb-3">Quality Improvement</h3>
            <ul className="space-y-2 text-purple-200">
              <li><strong>Relevant content:</strong> Test cases from most relevant sections</li>
              <li><strong>Technique-specific:</strong> Content tailored to each approach</li>
              <li><strong>Better coverage:</strong> Different sections for different techniques</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Implementation Details */}
      <div id="implementation" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Code className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🔧 Implementation Details</h2>
        </div>

        <div className="space-y-4">
          {/* Key Methods */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Key Methods</h3>
            <ol className="list-decimal list-inside space-y-2 text-slate-200">
              <li><code className="bg-slate-600 px-2 py-1 rounded text-slate-200">analyze_document_structure()</code> - Main analysis entry point</li>
              <li><code className="bg-slate-600 px-2 py-1 rounded text-slate-200">select_relevant_content()</code> - Content selection for specific techniques</li>
              <li><code className="bg-slate-600 px-2 py-1 rounded text-slate-200">_extract_pdf_sections()</code> - PDF-specific section extraction</li>
              <li><code className="bg-slate-600 px-2 py-1 rounded text-slate-200">_find_relevant_sections()</code> - Section scoring and ranking</li>
              <li><code className="bg-slate-600 px-2 py-1 rounded text-slate-200">_combine_sections()</code> - Content combination with length limits</li>
            </ol>
          </div>

          {/* Caching Strategy */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Caching Strategy</h3>
            <ul className="space-y-2 text-slate-200">
              <li>• <strong>Document-level caching:</strong> Based on content hash</li>
              <li>• <strong>Memory-based storage:</strong> In-memory dictionaries</li>
              <li>• <strong>Automatic cleanup:</strong> Can be extended with TTL</li>
              <li>• <strong>Cache invalidation:</strong> Manual or time-based</li>
            </ul>
          </div>

          {/* Error Handling */}
          <div className="border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-100 mb-3">Error Handling</h3>
            <ul className="space-y-2 text-slate-200">
              <li>• <strong>Graceful degradation:</strong> Falls back to basic structure if analysis fails</li>
              <li>• <strong>Content validation:</strong> Ensures minimum content length</li>
              <li>• <strong>Logging:</strong> Comprehensive logging for debugging</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Usage Example */}
      <div id="usage" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Code className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">🚀 Usage Example</h2>
        </div>

        <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
          <pre className="text-sm">
{`# Initialize service
service = TestCaseGenerationService()

# Analyze document (cached)
document_structure = service.analyze_document_structure(content, file_path)

# Select relevant content for specific technique
relevant_content = service.select_relevant_content(
    document_structure, 
    "Knowledge Base", 
    "Factual Testing",
    max_content_length=2000
)

# Generate test cases using only relevant content
test_cases = service._generate_test_cases_for_technique(
    relevant_content, technique, num_cases
)`}
          </pre>
        </div>
      </div>

      {/* Future Enhancements */}
      <div id="future" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <TrendingUp className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">📋 Future Enhancements</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ul className="space-y-2 text-slate-200">
            <li>• <strong>Advanced NLP:</strong> Use spaCy or NLTK for better keyword extraction</li>
            <li>• <strong>Semantic Search:</strong> Implement vector-based content similarity</li>
            <li>• <strong>Dynamic Caching:</strong> Add TTL and memory management</li>
          </ul>
          <ul className="space-y-2 text-slate-200">
            <li>• <strong>Content Prioritization:</strong> Weight sections by importance</li>
            <li>• <strong>Multi-language Support:</strong> Extend to other languages</li>
            <li>• <strong>Performance Metrics:</strong> Add timing and token usage tracking</li>
          </ul>
        </div>
      </div>

      {/* Results Summary */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-purple-200 mb-4">🎉 Results Summary</h2>
        <p className="text-slate-300 mb-4">
          The hybrid approach provides significant improvements in efficiency and quality:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ul className="space-y-2 text-slate-200">
            <li>• <strong>60-80% reduction</strong> in token usage</li>
            <li>• <strong>3-5x faster</strong> processing for multiple techniques</li>
          </ul>
          <ul className="space-y-2 text-slate-200">
            <li>• <strong>Higher quality</strong> test cases from relevant content</li>
            <li>• <strong>Better scalability</strong> for large documents</li>
          </ul>
        </div>
      </div>

      {/* Test Context */}
      <div id="test-context" className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <BookOpen className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-semibold text-slate-100">📚 Test Context</h2>
        </div>

        <div className="space-y-6 text-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Menu Items</h3>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Test Types</strong> (/context/test-types): Define categories of tests with optional purpose and prompt templates.</li>
              <li><strong>Test Techniques</strong> (/context/test-techniques): Define specific approaches linked to a Test Type.</li>
              <li><strong>Evaluation Mappings</strong> (/context/evaluation-mappings): Choose which metrics evaluate each Technique and set weights/priority.</li>
              <li><strong>Evaluation Frameworks</strong> (/context/evaluation-frameworks): Manage metric providers (e.g., RAGAS, OpenAI) and their metric catalogs.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Data Relationships</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Test Type 1—N Test Technique</li>
              <li>Test Technique 1—N Evaluation Mapping (each mapping references a metric name/type)</li>
              <li>Evaluation Framework 1—N Framework Metric (catalog of available metrics)</li>
            </ul>
            <p className="mt-2">Mappings reference metrics by name/type from active frameworks; framework/metric status controls availability.</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Typical Workflow</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>Create Test Types (names, categories, optional prompts/purpose).</li>
              <li>Add Techniques and associate each with a Test Type.</li>
              <li>In Evaluation Frameworks, define frameworks and their metrics (mark active).</li>
              <li>In Evaluation Mappings, attach metrics to Techniques; set required, weights, and priority.</li>
            </ol>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Runtime Connections</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Techniques render with their Test Type name/category (server-side joins in grouped listing).</li>
              <li>Evaluation Mappings shows techniques and available metrics aggregated from active frameworks/metrics.</li>
              <li>Mappings store metric identity by name/type so evaluators route to the correct framework.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">Impact on Evaluation</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Technique mappings determine which metrics run and their weighting in aggregate scores.</li>
              <li>Framework settings (timeouts, batch, score ranges) affect execution and normalization.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">References</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>UI: Test Types, Test Techniques, Evaluation Mappings, Evaluation Frameworks components.</li>
              <li>API: test_types.py, test_techniques.py, evaluation_mappings.py, evaluation_frameworks.py.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;

